'use client';

import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { BedDouble, Briefcase, CalendarCheck, Shield, Sparkles, User as UserIcon } from 'lucide-react';

import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { editorBandSurface } from '@/lib/editorial';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuthUser } from '@/hooks/use-auth-user';

const gold = 'text-[#daba7b]';
const dim = 'text-[#f5f2ed]/55';

const tabClass =
  'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium tracking-wide whitespace-nowrap transition-colors';

/**
 * Bar height (content + iOS safe area). Published on <html> as
 * --nl-bottom-nav-h while the bar is visible, so every other bottom-anchored
 * layer (cookie banner, toast viewport, admin shell) can offset itself with
 * `var(--nl-bottom-nav-h, 0px)` instead of hardcoding this value.
 */
const NAV_HEIGHT = 'calc(3.75rem + env(safe-area-inset-bottom))';

interface Roles {
  isAdmin: boolean;
  isAgent: boolean;
}
const NO_ROLES: Roles = { isAdmin: false, isAgent: false };

/**
 * Bottom tab bar for signed-in users on mobile – one-tap access to the
 * primary destinations that otherwise live inside the AuthButton dropdown
 * at the bottom of the hamburger sheet. Hidden at md and up, hidden for
 * anonymous visitors (they keep the marketing-oriented sheet nav). The third
 * tab is role-aware: admins get Admin, agents get Agent Dashboard, everyone
 * else gets Experiences. The Account tab opens /account, which holds the
 * profile, dashboards for dual-role users, legal pages, sign-out and account
 * deletion. Shown on /auth routes too: below md this bar is the only way
 * there.
 */
export default function MobileTabBar() {
  const pathname = usePathname();
  const user = useAuthUser();
  const isMobile = useIsMobile();
  const [roles, setRoles] = React.useState<Roles>(NO_ROLES);

  // Resolve roles only where the bar is actually visible (signed-in + <md);
  // desktop users shouldn't pay a profile round-trip for an invisible nav.
  React.useEffect(() => {
    setRoles(NO_ROLES);
    if (!user || !isMobile) return;
    let cancelled = false;
    (async () => {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const { getNormalizedUserProfile } = await import('@/lib/actions');
        const profile = await getNormalizedUserProfile(idTokenResult.token);
        // Ignore late responses from a signed-out or switched account.
        if (cancelled || auth.currentUser?.uid !== user.uid) return;
        setRoles({
          isAdmin: !!idTokenResult.claims.isAdmin,
          isAgent: !!profile?.isAgent,
        });
      } catch (error) {
        console.error('[MobileTabBar] Error resolving user roles:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isMobile]);

  // Publish the bar height so other fixed bottom layers can clear it.
  React.useEffect(() => {
    if (!user || !isMobile) return;
    document.documentElement.style.setProperty('--nl-bottom-nav-h', NAV_HEIGHT);
    return () => {
      document.documentElement.style.removeProperty('--nl-bottom-nav-h');
    };
  }, [user, isMobile]);

  if (!user) return null;

  const roleTab = roles.isAdmin
    ? { href: '/admin', label: 'Admin', icon: Shield }
    : roles.isAgent
      ? { href: '/agent-dashboard', label: 'Agent', icon: Briefcase }
      : { href: '/initiatives', label: 'Experiences', icon: Sparkles };

  const tabs = [
    { href: '/hotels', label: 'Hotels', icon: BedDouble },
    { href: '/bookings', label: 'Bookings', icon: CalendarCheck },
    roleTab,
  ];

  const isActive = (href: string) => pathname === href || pathname?.startsWith(`${href}/`);
  // The account page and the pages it leads to light up the Account tab.
  const accountActive = ['/account', '/auth/complete-profile', '/subscription'].some(isActive);

  return (
    <>
      {/* In-flow spacer so the page bottom (footer end) clears the fixed bar. */}
      <div aria-hidden className="md:hidden" style={{ height: NAV_HEIGHT }} />
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-white/10 pb-[env(safe-area-inset-bottom)] md:hidden"
        style={editorBandSurface}
      >
        {tabs.map((tab) => {
          const active = isActive(tab.href);
          return (
            <Link
              key={tab.href}
              href={tab.href}
              aria-current={active ? 'page' : undefined}
              className={cn(tabClass, active ? gold : dim, 'hover:text-[#daba7b]')}
            >
              <tab.icon className="h-5 w-5" aria-hidden />
              {tab.label}
            </Link>
          );
        })}

        <Link
          href="/account"
          aria-current={accountActive ? 'page' : undefined}
          className={cn(tabClass, accountActive ? gold : dim, 'hover:text-[#daba7b]')}
        >
          <UserIcon className="h-5 w-5" aria-hidden />
          Account
        </Link>
      </nav>
    </>
  );
}
